/** @module csi/store/Request */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"dojo/when",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/comm/PostRequest",
	"csi/utility/coreUtility",
	"./QueryResults"
], function(declare, lang, Memory, when, csi, Deferred, _, PostRequest, {objectsAreEqual}, QueryResults) {

	const _memoryStoreOptions = ["data", "idProperty", "queryEngine"];

	return declare(null, {
		// Function(item): optional function to augment store item when it is put into store
		//augmentItem: null,

		// Booean: if true, then client will handle filtering and sorting rather than server request
		// FIX: can this be changed to query option so that same store can do both server and client filtering based on query options?
		//bSimpleQueryEngine: false,

		// String: property to use to get unique id - same as _store.idProperty
		idProperty: "$URL",

		// String: property to use to get name
		labelProperty: "$Name",

		// PostRequest: request to issue to server - reset to null after first issued, so needs to be reset
		// before each call to query where you want to go to server; otherwise query is resolved against existing data
		// NOTE: do not use in conjunction with requestModule, requestOperation, requestParameters
		//request: null,

		// String: request parameter for binary content
		//requestBinaryParameter: null,

		// String: module and operation parameters for request
		//requestModule: null,
		//requestOperation: null,

		// String: special operation to use to get request count only (support for "countOnly" query option)
		//requestOperationCount: null,

		// String[]: array of keys to omit from options object that do not apply to server request
		requestOmitOptions: ["direction", "noActionData", "queryOptions", "store"],

		// String[]: array of keys to omit from query object that do not apply to server request
		//requestOmitQuery: null,

		// Object: extra parameters used to build request that take precedence over options passed into query
		//requestParameters: null,

		// Deferred[]: array of deferred queries that occurred while data was being requested
		//_dfdQueries: null,

		// Deferred: deferred object for issuing request
		//_dfdRequest: null,

		// Object: previous query options used to compare against new query
		//_lastQuery: null,
		//_lastOptions: null,

		// Boolean: true if the data is loaded
		//_loaded: false,

		// Deferred: deferred created when data is filled on client before response from server; resolved when server response is finally processed
		//_prefilled: null,

		// Memory: memory store used for holding data
		// FIX - rename to cachingStore?
		//_store: null,

		constructor: function(/*Object*/options) {
			// summary: create the memory store with only the options in _memoryStoreOptions
			options = options || {};
			options.idProperty = options.idProperty || this.idProperty;
			this._onStoreOptions(options);
			lang.mixin(this, _.omit(options, _memoryStoreOptions));
			this._store = this._store || new Memory(_.pick(options, _memoryStoreOptions));

			// needed to support store API
			this.idProperty = this._store.idProperty;
			this.queryEngine = this.queryEngine || this._store.queryEngine;

			// if full request passed in, then disable automatic generation of request
			if (this.request) {
				this.requestModule = null;
			}
		},

		add: function(object, directives) {
			return this._store.add(object, directives);
		},

		/**
		 * Add parameter to requests from this store
		 * @param {string} name 
		 * @param {string} value 
		 */
		addRequestParameter(name, value) {
			if (value != null) {
				this.requestParameters ||= {};
				this.requestParameters[name] = value;
			}
		},

		get: function(/*String*/id) {
			// summary: if already in cache, return it
			var obj = this._store.get(id);
			if (obj) return obj;

			// if not in cache and no request to get it, then return null
			if (!this.request && !this._dfdRequest) {
				return null;
			}

			// if there is no object in the cache, query for it
			var q = {};
			q[this.idProperty] = id;

			return when(this.query(q, {noActionData: true}), function(results) {
				return results[0];
			});
		},

		getEmptyValue: function() {
			// summary: return empty value for store depending on idProperty
			return (this.idProperty == this.labelProperty) ? "" : csi.EMPTY_URL;
		},

		getFromMemory: function(/*String*/id) {
			// summary: return item only if in memory - will not issue request if store is empty
			return this._store.get(id);
		},

		getIdentity: function(object) {
			// summary: return identity property of object
			return object ? object[this.idProperty] : null;
		},

		getRequest: function(/*Object?*/query, /*Object?*/options, /*Boolean?*/bReport, /*Boolean?*/bForce) {
			// summary: generate server request for given query and options, or override and just return any Deferred
			if (!this.requestModule) {
				return null;
			}

			// determine which options and query should be processed by server request, and which will be processed by client
			var serverQuery = this._getServerQuery(query);
			var serverOptions = this._getServerOptions(options);

			if (!bReport && !bForce) {
				if (!this._isDirty(serverQuery, serverOptions)) {
					return null;
				}

				// keep track of query and options that will be used for next request
				this._lastQuery = serverQuery;
				this._lastOptions = serverOptions;
			}

			// query and options are different than before, so generate request for server
			var params = {};

			if (options?.requestParameters) {
				lang.mixin(params, options.requestParameters);
			}

			// any options explicitly on store should override the options coming from view
			if (this.requestParameters) {
				lang.mixin(params, this.requestParameters);
			}

			var module = params.Module || this.requestModule;
			var operation = params.Operation || this.requestOperation;

			delete params.Module;
			delete params.Operation;

			var request = new PostRequest(module, operation, params);

			if (this.requestBinaryParameter) {
				request.setBinaryQuery(this.requestBinaryParameter, this._getBinary(serverQuery, serverOptions, bReport));
			}

			return request;
		},

		includesQuery: function(/*Object*/query) {
			// summary: determine if this store would include data necessary to satisfy given query based on store's last query
			var lastQuery = this._lastQuery;

			// first, test size of queries; if last query has more conditions than new query, then the answer is false -
			// it is ok for new query to have more conditions than the last query
			var n = _.size(query);
			var nLast = _.size(lastQuery);

			if (nLast > n) {
				return false;
			}

			// second, test each condition; if keys are different, or values of each key are different, then answer is also false
			return _.every(this._lastQuery, function(value, key) {
				return _.isEqual(query[key], value);
			});
		},

		/** exclude the empty item - "no results"  */
		internalQuery(query, options) {
			return this.query(query, {...options, noActionData: true});
		},

		async ensureStoreIsLoaded() {
			if (!this.isLoaded()) {
				await this.asyncQuery();
			}
		},

		isLoaded: function() {
			// summary: return true if store has already issued request and loaded data
			return !this.request && !this._dfdRequest && this._loaded;
		},

		/**
		 * Return true if given query differs from lasy query object used to populate store
		 * @param {Record<string, any>} query 
		 * @return {boolean}
		 */
		isQueryDirty(query) {
			return !objectsAreEqual(query, this._lastQuery, true);
		},

		put: function(object, directives) {
			// summary: call function if available when adding item to store; provides ability to augment item with extra attributes
			// or transform item as necessary
			if (this.augmentItem) {
				this.augmentItem(object);
			}

			return this._store.put(object, directives);
		},

		query: function(query, options) {
			// summary: query store with given options - if "query" is different than previous, then generate new server
			// request to process new "query". Set options.useCache=true to bypass this behavior and instead query what
			// is already loaded in store.

			// FIX: should we really check not generate new request if already waiting for another request?
			if (!this.request && !this._dfdRequest && !this._prefilled && (!options || !options.useCache)) {
				this.request = this._createQueryRequest(query, options);
			}

			const {request} = this;
			if (request) {
				// cancel current requests if issuing another
				this.cancelRequest();
				this.request = null;

				// notify listeners that request is being issued
				this.onRequest(query, options);

				if (request.post) {
					// request is XHR request - must pass load/error functions as parameter for sync case to work;
					// in that case, response is returned instead of Deferred (should fix that!)
					request.allowRetry = true;

					this._dfdRequest = request.post({
						load: lang.hitch(this, this._onRequestLoad, request, options),
						error: lang.hitch(this, this._onRequestError),
						sync: options && options.sync
					});
				} else {
					// request is just a Deferred
					this._dfdRequest = request;

					request.then(
						lang.hitch(this, this._onRequestData, query, options),
						lang.hitch(this, this._onRequestError)
					);
				}

				if (options && options.sync) {
					// if synchronous request, then store should already be populated so no need to track request;
					// sync is currently supported for some form use cases (such as determining if there is only one
					// result, then select it automatically)
					this._dfdRequest = null;
				} else if (!this.isLoaded() && this._prefillData(query, options)) {
					// prefill store when possible; if successful, then do query immediately rather than wait for server request
					this._prefilled = this._dfdRequest;
					this._dfdRequest = null;
				}
			}

			if (query) {
				if (!this.bSimpleQueryEngine && this.requestOmitQuery) {
					// if query is not fully for client, then only filter on client with those parameters not for the server
					query = _.pick(query, this.requestOmitQuery);
				} else if (this._dfdRequest) {
					// if request is delayed, clone query so that it won't be changed while waiting
					query = _.clone(query);
				}
			}

			if (this._dfdRequest) {
				// request is already issued, so wait for response before processing query
				this._dfdQueries = this._dfdQueries || [];

				var uid = csi.getUniqueId();
				var dfd = new Deferred(lang.hitch(this, this._onCancel, uid));
				dfd.requestId = uid;
				dfd.requestQuery = query;

				// isolate options from different queries in case of options modifications
				dfd.requestOptions = _.clone(options);

				// if secondary request's options will be modified, make changes available for original request listeners
				// see CheckedMultiselectDropdown->groupItems() 
				if (options) {
					options.nextResponseOptions = dfd.requestOptions;
				}

				this._dfdQueries.push(dfd);

				return QueryResults(dfd);
			}

			// use existing data to fulfill query
			this._loaded = true;
			const result = this._doQuery(query, options);

			// add prefilled deferred to result so that consumer can wait for server response and react accordingly
			if (this._prefilled) {
				result.secondaryResponse = this._prefilled;
			}

			return result;
		},

		/**
		 * Adapter to query store asynchronously
		 * @param {object} [query]
		 * @param {object} [options] 
		 * @return {Promise<object[]} 
		 */
		asyncQuery: async function(query, options) {
			return new Promise((resolve, reject) => {
				when(this.query(query, options),
					(results) => {
						resolve(Array.from(results));
					},
					reject
				);
			});
		},

		remove: function(id) {
			return this._store.remove(id);
		},

		reset: function() {
			// summary: reset store so that request will be issued on next query
			this._loaded = false;
		},

		onRequest: function(/*Object*/query, /*Object*/options) {
			// summary: extension point to call before request sent to server
		},

		onResponse: function(/*Response*/res) {
			// summary: extension point to call after client processes response from server
		},

		cancelRequest: function() {
			// summary: cancel current request if any
			var req = this._dfdRequest || this._prefilled;
			if (req) {
				req.cancel();

				this._dfdRequest = null;
				this._prefilled = null;

				if (this._dfdQueries) {
					_.each(this._dfdQueries, function(d) {d.cancel();});
					delete this._dfdQueries;
				}
			}
		},

		_createQueryRequest: function(/*Object?*/query, /*Object?*/options) {
			// summary: by default construct request if query and options are different than those previously used
			return this.getRequest(query, options);
		},

		_doQuery: function(query, options) {
			// summary: query memory store with options
			return this._store.query(query, options);
		},

		_getBinary: function(/*Object*/query, /*Object*/options, /*Boolean*/bReport) {
			// summary: generate XML string for server request based on given query and options
			console.assert(false, "Need to override _getBinary for store to generate value for: " + this.requestBinaryParameter);
			return "";
		},

		_getServerOptions: function(/*Object?*/options) {
			// summary: return query options that will be applied on server
			var serverOptions = options;

			if (serverOptions && this.bSimpleQueryEngine) {
				serverOptions = _.omit(serverOptions, ["sort"]);
			}

			if (serverOptions && this.requestOmitOptions) {
				serverOptions = _.omit(serverOptions, this.requestOmitOptions);
			}

			return serverOptions ? _.clone(serverOptions) : null;
		},

		_getServerQuery: function(/*Object?*/query) {
			// summary: return query object that will be applied on server
			var serverQuery = this.bSimpleQueryEngine ? null : query;

			if (serverQuery && this.requestOmitQuery) {
				serverQuery = _.omit(serverQuery, this.requestOmitQuery);
			}

			return serverQuery ? _.clone(serverQuery) : null;
		},

		_isDirty: function(/*Object*/query, /*Object*/options) {
			// summary: determine if query options require new request
			return !this._loaded || this.isQueryDirty(query) || !objectsAreEqual(options, this._lastOptions, true);
		},

		_onCancel: function(/*String*/uid) {
			// summary: remove query by id
			this._dfdQueries.dele(function(d) {return d.requestId == uid;});
		},

		_onData: function(/*Object[]*/data, /*Object*/options) {
			// summary: extension point to set store data for given response - return Deferred object
			// if necessary to delay execution of queries further
			this._setData(data || []);
		},

		_onRequestError: function(/*Error*/err) {
			// summary: pass error through
			this._dfdRequest = null;
			this._prefilled = null;

			_.each(this._dfdQueries, function(d) {
				d.reject(err);
			}, this);

			delete this._dfdQueries;
		},

		_onRequestLoad: function(/*Request*/req, /*Object*/options, /*Response*/res) {
			// summary: set data according to response and then resolve queries issued during request
			if (this._prefilled) {
				// if prefilled with data, then process server request and then resolve deferred
				this._onResponse(res, req, options);
				this.onResponse(res);
				this._prefilled.resolve();
				this._prefilled = null;
			} else {
				when(this._onResponse(res, req, options), lang.hitch(this, this._resolveQueries, res));
			}
		},

		_onRequestData: function(/*Object*/query, /*Object*/options, /*Object[]*/data) {
			// summary: set data according to response and then resolve queries issued during request
			when(this._onData(data, options), lang.hitch(this, this._resolveQueries, null));
		},

		_onResponse: function(/*Response*/res, /*Request*/req, /*Object*/options) {
			// summary: extension point to set store data for given response - return Deferred object
			// if necessary to delay execution of queries further
			if (res.getResultNodes) {
				this._setData(res.getResultNodes());
			}
		},

		_onStoreOptions: function(/*Object*/options) {
			// summary: update store options with additional properties
		},

		/**
		 * Prefill store with data if possible so query can return more quickly; this allows for ability of progressive enhancement
		 * with store results. Return true if successful.
		 * @param {Object} [query]
		 * @param {Object} [options]
		 * @return {boolean}
		 */
		_prefillData: function(query, options) {
			return false;
		},

		_resolveQueries: function(/*Response?*/res) {
			// summary: resolve outstanding queries now that store is loaded
			var queries = this._dfdQueries;

			this._dfdQueries = null;
			this._dfdRequest = null;
			this._loaded = true;

			this.onResponse(res);

			_.each(queries, function(d) {
				d.resolve(this._doQuery(d.requestQuery, d.requestOptions));
			}, this);
		},

		_setData: function(/*Object[]*/items) {
			// summary: add items to caching store - do not use "put" api for main query results as that will disrupt Observable stores
			if (this.augmentItem) {
				_.each(items, this.augmentItem, this);
			}

			this._store.setData(items);
		}
	});
});